import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
    FaPlus, FaEdit, FaTrash, FaRocket, FaWrench, FaBug, FaTools,
    FaChevronLeft, FaChevronRight, FaLightbulb, FaBullhorn,
    FaCheckCircle, FaTimesCircle, FaCog, FaFire, FaClock
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import UpdateFormModal from './UpdateFormModal';
import './SystemHubUpdates.css';
import { useSocket } from '../components/SocketContext';

// ─── Configuração de categorias ───────────────────────────────────────────────
const CATEGORY_CONFIG = {
    NOVO:     { icon: <FaRocket />,  label: 'Novidade',    cls: 'cat-novo'     },
    MELHORIA: { icon: <FaWrench />,  label: 'Melhoria',    cls: 'cat-melhoria' },
    CORRECAO: { icon: <FaBug />,     label: 'Correção',    cls: 'cat-correcao' },
    GMUD:     { icon: <FaTools />,   label: 'Manutenção',  cls: 'cat-gmud'     },
};

const STATUS_CONFIG = {
    PENDENTE:         { icon: <FaLightbulb />,              label: 'Em Votação',       cls: 'sug-pendente'    },
    EM_DESENVOLVIMENTO:{ icon: <FaCog className="fa-spin"/>, label: 'Desenvolvendo',    cls: 'sug-dev'         },
    CONCLUIDA:        { icon: <FaCheckCircle />,            label: 'Concluída',        cls: 'sug-concluida'   },
    REJEITADA:        { icon: <FaTimesCircle />,            label: 'Encerrada',        cls: 'sug-rejeitada'   },
};

const getCategoryKey = (update) =>
    update.tipo === 'gmud' ? 'GMUD' : update.categoria;

// ─── Card de Atualização ──────────────────────────────────────────────────────
const UpdateCard = ({ update, onEdit, onDelete, index }) => {
    const key    = getCategoryKey(update);
    const config = CATEGORY_CONFIG[key] || CATEGORY_CONFIG.MELHORIA;
    const version = update.tipo === 'manual' && update.versao ? update.versao : null;

    return (
        <article
            className={`shub-card ${config.cls}`}
            style={{ animationDelay: `${index * 60}ms` }}
        >
            <div className="shub-card__stripe" />

            <div className="shub-card__header">
                <div className={`shub-cat-badge ${config.cls}`}>
                    <span className="shub-cat-badge__icon">{config.icon}</span>
                    <span className="shub-cat-badge__label">{config.label}</span>
                    {version && <span className="shub-cat-badge__version">{version}</span>}
                </div>
                <div className="shub-card__actions">
                    <button className="shub-icon-btn shub-icon-btn--edit"
                        onClick={() => onEdit(update)} title="Editar">
                        <FaEdit />
                    </button>
                    <button className="shub-icon-btn shub-icon-btn--delete"
                        onClick={() => onDelete(update.id)} title="Excluir">
                        <FaTrash />
                    </button>
                </div>
            </div>

            <div className="shub-card__body">
                <h3 className="shub-card__title">{update.titulo}</h3>
                {update.imagem_url && (
                    <img src={update.imagem_url} alt={update.titulo} className="shub-card__image" />
                )}
                <div
                    className="shub-card__desc"
                    dangerouslySetInnerHTML={{ __html: update.descricao }}
                />
            </div>

            <div className="shub-card__footer">
                <span className="shub-card__author">
                    <strong>{update.publicado_por_nome || 'Admin'}</strong>
                </span>
                <span className="shub-card__date">
                    <FaClock />
                    {new Date(update.data_publicacao).toLocaleString('pt-BR', {
                        day: '2-digit', month: 'short', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                    })}
                </span>
            </div>
        </article>
    );
};

// ─── Card de Sugestão ─────────────────────────────────────────────────────────
const SuggestionCard = ({ sug, onStatusChange, onDelete, index }) => {
    const statusConf = STATUS_CONFIG[sug.status] || STATUS_CONFIG.PENDENTE;
    const isHot = sug.votos >= 10;

    return (
        <div
            className="shub-sug-card"
            style={{ animationDelay: `${index * 50}ms` }}
        >
            {/* Contador de votos */}
            <div className="shub-sug-votes">
                <span className="shub-sug-votes__num">{sug.votos}</span>
                <span className="shub-sug-votes__label">votos</span>
                {isHot && <span className="shub-sug-votes__hot"><FaFire /></span>}
            </div>

            {/* Conteúdo */}
            <div className="shub-sug-content">
                <div className="shub-sug-content__top">
                    <span className={`shub-sug-status ${statusConf.cls}`}>
                        {statusConf.icon} {statusConf.label}
                    </span>
                </div>
                <p className="shub-sug-content__text">{sug.texto}</p>
                <div className="shub-sug-content__meta">
                    <strong>{sug.autor_nome}</strong>
                    <span className="shub-sug-content__company">· {sug.empresa_nome}</span>
                    <span className="shub-sug-content__date">
                        · {new Date(sug.criado_em).toLocaleDateString('pt-BR')}
                    </span>
                </div>
            </div>

            {/* Moderação */}
            <div className="shub-sug-mod">
                <select
                    className="shub-sug-select"
                    value={sug.status}
                    onChange={e => onStatusChange(sug.id, e.target.value)}
                >
                    <option value="PENDENTE">Em Votação</option>
                    <option value="EM_DESENVOLVIMENTO">Em Desenvolvimento</option>
                    <option value="CONCLUIDA">Concluída</option>
                    <option value="REJEITADA">Encerrar / Recusar</option>
                </select>
                <button
                    className="shub-sug-delete"
                    onClick={() => onDelete(sug.id)}
                >
                    <FaTrash /> Excluir
                </button>
            </div>
        </div>
    );
};

// ─── Componente principal ─────────────────────────────────────────────────────
export default function SystemHubUpdates() {
    const [activeTab, setActiveTab]           = useState('updates');
    const [updates, setUpdates]               = useState([]);
    const [loadingUpdates, setLoadingUpdates] = useState(true);
    const [isModalOpen, setIsModalOpen]       = useState(false);
    const [editingUpdate, setEditingUpdate]   = useState(null);
    const [currentPage, setCurrentPage]       = useState(1);
    const [totalPages, setTotalPages]         = useState(1);
    const [suggestions, setSuggestions]       = useState([]);
    const [loadingSugg, setLoadingSugg]       = useState(true);

    const LIMIT = 10;
    const socket = useSocket();

    // Fetch updates
    const fetchUpdates = useCallback(async (page = 1) => {
        try {
            setLoadingUpdates(true);
            const { data } = await api.get(`/api/system-hub/updates?page=${page}&limit=${LIMIT}`);
            setUpdates(data.items);
            setTotalPages(data.totalPages);
            setCurrentPage(data.page);
        } catch {
            toast.error('Falha ao buscar publicações.');
        } finally {
            setLoadingUpdates(false);
        }
    }, []);

    // Fetch suggestions
    const fetchSuggestions = useCallback(async () => {
        try {
            setLoadingSugg(true);
            const { data } = await api.get('/api/system-hub/suggestions');
            const list = data.items || (Array.isArray(data) ? data : []);
            setSuggestions(list.sort((a, b) => b.votos - a.votos));
        } catch {
            toast.error('Falha ao buscar ideias.');
        } finally {
            setLoadingSugg(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'updates') fetchUpdates(currentPage);
        else fetchSuggestions();
    }, [activeTab, currentPage, fetchUpdates, fetchSuggestions]);

    // Socket.IO
    useEffect(() => {
        if (!socket) return;
        const onNewUpdate = (d) => {
            toast.info(`Nova atualização: "${d.titulo}"`);
            if (activeTab === 'updates' && currentPage === 1) {
                setUpdates(prev => {
                    const next = [d, ...prev];
                    if (next.length > LIMIT) next.pop();
                    return next;
                });
            }
        };
        const onSuggChange = () => { if (activeTab === 'suggestions') fetchSuggestions(); };

        socket.on('new_system_update',         onNewUpdate);
        socket.on('new_suggestion',            onSuggChange);
        socket.on('suggestion_voted',          onSuggChange);
        socket.on('suggestion_status_changed', onSuggChange);
        return () => {
            socket.off('new_system_update',         onNewUpdate);
            socket.off('new_suggestion',            onSuggChange);
            socket.off('suggestion_voted',          onSuggChange);
            socket.off('suggestion_status_changed', onSuggChange);
        };
    }, [socket, activeTab, currentPage, fetchSuggestions]);

    // Modal handlers
    const openModal  = (update = null) => { setEditingUpdate(update); setIsModalOpen(true); };
    const closeModal = ()              => { setEditingUpdate(null);   setIsModalOpen(false); };
    const afterSave  = ()              => { fetchUpdates(currentPage); closeModal(); };

    // Delete update
    const deleteUpdate = async (id) => {
        if (!window.confirm('Excluir esta publicação?')) return;
        try {
            await api.delete(`/api/system-hub/updates/${id}`);
            toast.success('Publicação excluída!');
            fetchUpdates(currentPage);
        } catch {
            toast.error('Falha ao excluir publicação.');
        }
    };

    // Suggestion moderation
    const updateSuggStatus = async (id, status) => {
        try {
            await api.put(`/api/system-hub/suggestions/${id}/status`, { status });
            toast.success('Status atualizado.');
            fetchSuggestions();
        } catch {
            toast.error('Falha ao atualizar status.');
        }
    };

    const deleteSuggestion = async (id) => {
        if (!window.confirm('Excluir esta ideia permanentemente?')) return;
        try {
            await api.delete(`/api/system-hub/suggestions/${id}`);
            toast.success('Ideia excluída.');
            fetchSuggestions();
        } catch {
            toast.error('Falha ao excluir ideia.');
        }
    };

    return (
        <div className="shub-page">

            {/* ── Topo ── */}
            <div className="shub-topbar">
                <div className="shub-topbar__left">
                    <div className="shub-topbar__icon-wrap">
                        <FaRocket />
                    </div>
                    <div>
                        <h1 className="shub-topbar__title">System Hub</h1>
                        <p className="shub-topbar__subtitle">Gestão de publicações e ideias da comunidade</p>
                    </div>
                </div>
                {activeTab === 'updates' && (
                    <button className="shub-btn-new" onClick={() => openModal()}>
                        <FaPlus /> Nova Publicação
                    </button>
                )}
            </div>

            {/* ── Abas ── */}
            <div className="shub-tabs">
                <button
                    className={`shub-tab ${activeTab === 'updates' ? 'shub-tab--active shub-tab--blue' : ''}`}
                    onClick={() => setActiveTab('updates')}
                >
                    <FaBullhorn />
                    <span>Atualizações & GMUDs</span>
                    {!loadingUpdates && (
                        <span className="shub-tab__count">{updates.length}</span>
                    )}
                </button>
                <button
                    className={`shub-tab ${activeTab === 'suggestions' ? 'shub-tab--active shub-tab--orange' : ''}`}
                    onClick={() => setActiveTab('suggestions')}
                >
                    <FaLightbulb />
                    <span>Ideias da Comunidade</span>
                    {!loadingSugg && (
                        <span className="shub-tab__count shub-tab__count--orange">{suggestions.length}</span>
                    )}
                </button>
            </div>

            {/* ── Conteúdo ── */}
            <div className="shub-body">

                {/* ABA: UPDATES */}
                {activeTab === 'updates' && (
                    loadingUpdates ? (
                        <div className="shub-loading">
                            <div className="shub-spinner" />
                            <p>Carregando publicações…</p>
                        </div>
                    ) : updates.length === 0 ? (
                        <div className="shub-empty">
                            <div className="shub-empty__icon"><FaRocket /></div>
                            <h2>Nenhuma publicação ainda</h2>
                            <p>Clique em "Nova Publicação" para começar a comunicar novidades ao time.</p>
                            <button className="shub-btn-new" onClick={() => openModal()}>
                                <FaPlus /> Criar primeira publicação
                            </button>
                        </div>
                    ) : (
                        <div className="shub-updates-grid">
                            {updates.map((u, i) => (
                                <UpdateCard
                                    key={u.id}
                                    update={u}
                                    index={i}
                                    onEdit={openModal}
                                    onDelete={
                                        u.tipo === 'manual'
                                            ? deleteUpdate
                                            : () => toast.warn('GMUDs não podem ser excluídas por aqui.')
                                    }
                                />
                            ))}

                            {totalPages > 1 && (
                                <div className="shub-pagination">
                                    <button
                                        className="shub-page-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(p => p - 1)}
                                    >
                                        <FaChevronLeft />
                                    </button>
                                    <span className="shub-page-info">
                                        Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
                                    </span>
                                    <button
                                        className="shub-page-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(p => p + 1)}
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            )}
                        </div>
                    )
                )}

                {/* ABA: SUGESTÕES */}
                {activeTab === 'suggestions' && (
                    loadingSugg ? (
                        <div className="shub-loading">
                            <div className="shub-spinner shub-spinner--orange" />
                            <p>Carregando banco de ideias…</p>
                        </div>
                    ) : suggestions.length === 0 ? (
                        <div className="shub-empty">
                            <div className="shub-empty__icon shub-empty__icon--orange"><FaLightbulb /></div>
                            <h2>Nenhuma ideia enviada</h2>
                            <p>As sugestões dos seus clientes aparecerão aqui para moderação.</p>
                        </div>
                    ) : (
                        <div className="shub-sugg-list">
                            {suggestions.map((s, i) => (
                                <SuggestionCard
                                    key={s.id}
                                    sug={s}
                                    index={i}
                                    onStatusChange={updateSuggStatus}
                                    onDelete={deleteSuggestion}
                                />
                            ))}
                        </div>
                    )
                )}
            </div>

            {/* ── Modal ── */}
            {isModalOpen && (
                <UpdateFormModal
                    isOpen={isModalOpen}
                    onClose={closeModal}
                    onSave={afterSave}
                    updateData={editingUpdate}
                />
            )}
        </div>
    );
}